import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Form,
  InputGroup,
  Spinner,
  Alert,
  Badge,
  Tab,
  Tabs,
  Modal,
  Carousel
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { adminLogsAPI, transportsAPI, driversCompanyAPI, transportOrdersAPI } from '../../api/apiClient';
import { useAuth } from "../../context/AuthContext";
import "../../assets/css/OrderDetails.css";
import Swal from 'sweetalert2';
import { 
  FaFacebookMessenger, 
  FaEye, 
  FaEyeSlash, 
  FaEuroSign, 
  FaCheck, 
  FaTimes, 
  FaBolt, 
  FaTrashAlt, 
  FaArrowLeft, 
  FaMoneyBillWave, 
  FaCog, 
  FaFileInvoice, 
  FaTruck, 
  FaSearch, 
  FaInfoCircle, 
  FaJediOrder, 
  FaLocationArrow,
  FaUser,
  FaMapMarkerAlt,
  FaBox,
  FaClipboardList,
  FaFileAlt,
  FaHistory,
  FaPlus,
  FaRoad,
  FaWeightHanging,
  FaExpand,
  FaImages,
  FaRulerCombined,
  FaCalendar,
  FaUserAlt
} from "react-icons/fa";
import { HiBoltSlash, HiWrenchScrewdriver } from "react-icons/hi2";
import { FaBoxesStacked } from "react-icons/fa6";
import { AiFillIdcard } from "react-icons/ai";
import { TiThList } from "react-icons/ti";
import { formatNumber } from '../../utils/helpers';
import { RiUserLocationFill } from "react-icons/ri";

const toBoolean = (value) => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return value === 1;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "1" || normalized === "true";
  }

  return false;
};

export default function TransportDetails() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const highlightId = location.state?.highlightId;
  
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [transportOrders, setTransportOrders] = useState(null);
  const [transport, setTransport] = useState(null);
  const [activeTab, setActiveTab] = useState("details");
  const [commission, setCommission] = useState(0);
  const [notizen, setMessage] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [updating, setUpdating] = useState(false);
  const [showDeleteLog, setShowDeleteLog] = useState(false);
  const [deleteLogId, setDeleteLogId] = useState(null);
  const fromPage = location.state?.fromPage || 1;
  const { isSuperAdmin, checkSuperAdminStatus } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [showDriversModal, setShowDriversModal] = useState(false);
  const [hasAvailableOrder, setHasAvailableOrder] = useState(false);
  const [hasAssignOrder, setHasAssignOrder] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sendSms, setSendSms] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [createOrderModal, setCreateOrderModal] = useState(false);
  const [orderData, setOrderData] = useState({
    price: '',
    info: ''
  });
  const hasAcceptedOrder = Boolean(transport?.order_status) || (
    Array.isArray(transportOrders) &&
    transportOrders.some((order) => ['assigned', 'received', 'completed'].includes(order.status))
  );

  useEffect(() => {
    fetchTransportDetails();
    fetchDrivers();
    checkSuperAdminStatus();
    fetchTransportOrder()
  }, [id]);


  const fetchTransportDetails = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await transportsAPI.show(id);
      
      if (response.success) {
        setTransport(response.data.data || response.data);
        setCommission(response.data.data?.platform_commission || 0);
        setMessage(response.data.data?.notizen || '');
      } else {
        setError(response.message || t('failed_to_fetch_transport_details'));
      }
    } catch (err) {
      setError(t('failed_to_fetch_transport_details'));
      console.error('Error fetching transport details:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransportOrder = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await transportOrdersAPI.byTransport(id);
      console.log(response);
      
      if (response.success) {
        const orders = response.data.data || response.data;
        setTransportOrders(orders);
        
        const assignOrder = orders.some(
          (order) => order.customer_id === null && order.status === "assigned"
        );
        setHasAssignOrder(assignOrder);
        
        const availableOrder = orders.some(order => order.customer_id !== null);
        setHasAvailableOrder(availableOrder);
      } else {
        setError(response.message || t('failed_to_fetch_transport_details'));
      }
    } catch (err) {
      setError(t('failed_to_fetch_transport_details'));
      console.error('Error fetching transport details:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDrivers = async (search = searchTerm) => {
    try {
      setLoading(true);
      const response = await driversCompanyAPI.index({
        search,
        per_page: 100 // Fetch a reasonable amount for the modal
      });
      if (response.success) {
        const fetchedData = response.data.data || response.data;
        setData(fetchedData);
        setDrivers(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching drivers:', err);
    } finally {
      setLoading(false);
    }
  };

  // Debounced Search Effect for Drivers
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm !== undefined && showDriversModal) {
        fetchDrivers(searchTerm);
      }
    }, 1500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, showDriversModal]);

  const selectDriver = async (driver) => {
    try {
      setUpdating(true);
      const response = await transportsAPI.assignDriver(id, { customer_id: driver.id });
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("driver_assigned_successfully"),
          timer: 3000,
          showConfirmButton: false,
        });
        setShowDriversModal(false);
        fetchTransportDetails();
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_assign_driver"),
        timer: 3000,
      });
    } finally {
      setUpdating(false);
    }
  };

  const createOrder = async () => {
    try {
      if (!orderData.price) {
        Swal.fire({
          icon: "warning",
          title: t("warning"),
          text: t("transport.please_fill_price"),
          timer: 3000,
        });
        return;
      }

      setUpdating(true);
      
      const requestData = {
        transport_id: id,
        price: orderData.price,
        info: orderData.info
      };

      const response = await transportsAPI.store(requestData);
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("create_order_successfully"),
          timer: 3000,
          showConfirmButton: false,
        });
        setCreateOrderModal(false);
        setOrderData({ price: '', info: '' }); 
        fetchTransportDetails();
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_create_order"),
        timer: 3000,
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleOrderDataChange = (field, value) => {
    setOrderData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSetPending = async () => {
    try {
      setUpdating(true);
      const response = await transportsAPI.setPending(id);
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("set_to_pending_successfully"),
          timer: 3000,
          showConfirmButton: false,
        });
        fetchTransportDetails();
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_update_status"),
        timer: 3000,
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleApprove = async () => {
    try {
      setUpdating(true);
      
      if (sendSms && sendEmail) {
        await transportsAPI.approve(id, { send_sms: 1, send_email: 1 });
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("booking.approved_sms_email_sent"),
          timer: 3000,
          showConfirmButton: false,
        });
      } else if (sendSms) {
        await transportsAPI.approve(id, { send_sms: 1 });
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("booking.approved_sms_sent"),
          timer: 3000,
          showConfirmButton: false,
        });
      } else if (sendEmail) {
        await transportsAPI.approve(id, { send_email: 1 });
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("booking.approved_email_sent"),
          timer: 3000,
          showConfirmButton: false,
        });
      } else {
        await transportsAPI.approve(id);
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("approved_successfully"),
          timer: 3000,
          showConfirmButton: false,
        });
      }
      
      fetchTransportDetails();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_approve"),
        timer: 3000,
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      Swal.fire({
        icon: "warning",
        title: t("warning"),
        text: t("please_enter_reject_reason"),
        timer: 3000,
      });
      return;
    }

    try {
      setUpdating(true);
      const response = await transportsAPI.reject(id, { rejected_reason: rejectReason });
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("rejected_successfully"),
          timer: 3000,
          showConfirmButton: false,
        });
        setShowRejectModal(false);
        setRejectReason('');
        fetchTransportDetails();
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_reject"),
        timer: 3000,
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleMarkPaid = async () => {
    try {
      setUpdating(true);
      const response = await transportsAPI.markPaid(id);
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("marked_paid_successfully"),
          timer: 3000,
          showConfirmButton: false,
        });
        fetchTransportDetails();
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_mark_paid"),
        timer: 3000,
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleAddCommission = async () => {
    try {
      if (commission < 0 || commission > 100) {
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: t("transport.commission_range_error"),
          timer: 3000,
        });
        return;
      }

      setUpdating(true);
      const response = await transportsAPI.addCommission(id, { 
        commission: commission 
      });
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("transport.commission_updated_successfully"), 
          timer: 3000,
          showConfirmButton: false,
        });
        
        fetchTransportDetails();
        
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_update_commission"),
        timer: 3000,
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleToggleCommission = async () => {
    if (!transport || hasAcceptedOrder) {
      return;
    }

    try {
      setUpdating(true);
      const nextApplyCommission = !toBoolean(transport.apply_commission);
      const response = await transportsAPI.toggleCommission(id, {
        apply_commission: nextApplyCommission,
      });

      if (response.success) {
        const payload = response.data?.data || response.data || {};
        const rawApplyCommission =
          payload.apply_commission ??
          payload.data?.apply_commission ??
          nextApplyCommission;
        const updatedApplyCommission = toBoolean(rawApplyCommission);

        setTransport((prev) =>
          prev ? { ...prev, apply_commission: updatedApplyCommission } : prev
        );

        Swal.fire({
          icon: "success",
          title: t("success"),
          text: updatedApplyCommission
            ? "تم تفعيل حساب نسبة المنصة"
            : "تم تعطيل حساب نسبة المنصة",
          timer: 3000,
          showConfirmButton: false,
        });
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || "فشل تحديث حساب نسبة المنصة",
        timer: 3000,
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleSendMessage = async () => {
    try {
      const response = await transportsAPI.sendNote(id, { notizen });
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("notes_submitted_successfully"),
          timer: 3000,
          showConfirmButton: false,
        });
        fetchTransportDetails();
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_send_note"),
        timer: 3000,
      });
    }
  };

  const handleViewTrack = (booking_id) => {
    navigate(`/admin/transports/track/${booking_id}`);
  };

  const handleViewLiveTracking = (id) => {
    navigate(`/admin/transports/live-tracking/${id}`);
  };

  const handleViewOrder = (id) => {
    navigate(`/admin/transports/orders-for-transport/${id}`);
  };

  const handleDeleteTransport = async () => {
    const result = await Swal.fire({
      title: t('are_you_sure'),
      text: t('confirm_delete_transport'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('delete'),
      cancelButtonText: t('cancel')
    });

    if (result.isConfirmed) {
      try {
        const response = await transportsAPI.delete(id);
        
        if (response.success) {
          Swal.fire({
            icon: 'success',
            title: t('deleted'),
            text: t('transport_deleted_successfully'),
            timer: 3000,
            showConfirmButton: false
          });
          navigate('/admin/transports');
        } else {
          throw new Error(response.message);
        }
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: t('error'),
          text: err.message || t('failed_to_delete_transport'),
          timer: 3000,
        });
      }
    }
  };

  const handleBack = () => {
    const state = {
      fromPage: location.state?.fromPage || 1,
      currentPage: location.state?.currentPage || 1,
      highlightId: location.state?.highlightId || parseInt(id),
      scrollToHighlight: true,
      activeTab: location.state?.activeTab || 'active'
    };
    
    console.log('Navigating back with state:', state);
    
    navigate('/admin/transports', { 
      state: state
    });
  };

  const confirmDeleteLog = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await adminLogsAPI.delete(deleteLogId);
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("data_deleted_successfully"),
          timer: 2000,
          showConfirmButton: false,
        });
        setShowDeleteLog(false);
        fetchTransportDetails();
      } else {
        setError(response.message || t("failed_to_delete_data"));
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: response.message || t("failed_to_delete_data"),
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      setError(t("failed_to_delete_data"));
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: t("failed_to_delete_data"),
        timer: 2000,
        showConfirmButton: false,
      });
      console.error("Error deleting log:", err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
        cancelled: { variant: 'danger', text: t('cancelled') },
        rejected: { variant: 'danger', text: t('rejected') },
        assigned: { variant: 'info', text: t('assigned') },
        received: { variant: 'secondary', text: t('received') },
        completed: { variant: 'success', text: t('completed') },
        pending: { variant: 'warning', text: t('pending') },
        approved: { variant: 'success', text: t('approved') }
      };
    
    const config = statusConfig[status] || { variant: 'light', text: status };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const getBooleanText = (value) => {
    if (value === null || value === undefined) return '-';
    if (value === 1 || value === '1') return t('yes');
    if (value === 0 || value === '0') return t('no');
    if (value === 2 || value === '2') return t('possible');
    return value;
  };

  const getLoadHelpingText = (value) => {
    if (value === null || value === undefined) return '-';
    if (value === 0 || value === '0') return t('no_help');
    if (value === 1 || value === '1') return t('partial_help_from_driver');
    if (value === 2 || value === '2') return t('help_from_driver_and_another_person');
    return value;
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const ImageGallery = ({ images, className = '' }) => {
    const [showModal, setShowModal] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const { t } = useTranslation();

    if (!images || images.length === 0) {
      return null;
    }

    const handleImageClick = (index) => {
      setSelectedIndex(index);
      setShowModal(true);
    };

    const handleCloseModal = () => {
      setShowModal(false);
    };

    return (
      <>
        <Card className="mb-4 shadow-sm">
          <Card.Header className="fw-bold card-title d-flex align-items-center">
            <FaImages className="me-2" />
            {t('images')} ({images.length})
          </Card.Header>
          <Card.Body>
            <div className={`image-gallery ${className}`}>
              <div className="d-flex flex-wrap gap-2">
                {images.slice(0, 4).map((image, index) => (
                  <div 
                    key={index}
                    className="thumbnail-container position-relative"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleImageClick(index)}
                  >
                    <img
                      src={image}
                      alt={`Transport ${index + 1}`}
                      className="thumbnail img-thumbnail"
                      style={{
                        width: '150px',
                        height: '150px',
                        objectFit: 'cover',
                        borderRadius: '8px'
                      }}
                    />
                    <div className="thumbnail-overlay">
                      <FaExpand className="text-white" size={12} />
                    </div>
                    
                    {/* مؤشر الصور الإضافية */}
                    {index === 3 && images.length > 4 && (
                      <div className="more-images-badge">
                        +{images.length - 4}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* Modal لتكبير الصور */}
        <Modal 
          show={showModal} 
          onHide={handleCloseModal}
          size="lg"
          centered
          className="image-modal"
        >
          <Modal.Header className="border-0 pb-0">
            <div className="d-flex justify-content-between align-items-center w-100">
              <h6 className="mb-0 text-dark">
                {t('images')} ({selectedIndex + 1} / {images.length})
              </h6>
              <Button
                variant="link"
                className="text-dark p-0"
                onClick={handleCloseModal}
              >
                <FaTimes size={20} />
              </Button>
            </div>
          </Modal.Header>
          
          <Modal.Body className="text-center p-0">
            <Carousel
              activeIndex={selectedIndex}
              onSelect={setSelectedIndex}
              interval={null}
              indicators={images.length > 1}
              controls={images.length > 1}
            >
              {images.map((image, index) => (
                <Carousel.Item key={index}>
                  <div className="d-flex justify-content-center align-items-center">
                    <img
                      src={image}
                      alt={`Transport ${index + 1}`}
                      className="img-fluid modal-image"
                      style={{
                        maxHeight: '70vh',
                        objectFit: 'contain',
                        borderRadius: '8px'
                      }}
                    />
                  </div>
                </Carousel.Item>
              ))}
            </Carousel>
          </Modal.Body>
        </Modal>
      </>
    );
  };

  // Header Section
  const renderHeader = () => {
    if (!transport) return null;

    return (
      <Card className="mb-4 shadow-sm card-header">
        <Card.Body className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
          <div className="card-title">
            <h2 className="mb-0">
              {t("booking.details")} - <span className="text-primary">{transport.booking_id}</span>
            </h2>
            <small>{t("booking.overview")}</small>
          </div>
          
          <div className="d-flex flex-wrap gap-1">
              {getStatusBadge(transport.status || 'pending')}

              <Badge bg={transport.payment_status ? 'success' : 'warning'} className="ms-2">
                {t(transport.payment) || t('unpaid')}
              </Badge>

              <Badge bg="info" className="ms-2">
                {t(transport.transport_type)}
              </Badge>

              <small className="text-white">
                {t('booking.created_on')}: {transport.created_at}
              </small>
          </div>
        </Card.Body>
      </Card>
    );
  };

  // Actions Section
  const renderActionsSection = () => {
    if (!transport) return null;

    return (
      <Card className="mb-4 shadow-sm">
        <Card.Header className="fw-bold card-title d-flex align-items-center">
          <FaCog className="me-2" />
          {t("booking.actions_section")}
        </Card.Header>
        <Card.Body>
          <Row>
            {/* Status Management */}
            <Col md={6} className="mb-3">
              <h6 className="fw-bold mb-3 text-primary">
                <FaCog className="me-2" />
                {t("booking.status_management")}
              </h6>
              <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
                {transport.status === 'pending' && (
                  <>
                    <Form.Check
                      type="switch"
                      id="send-sms-switch"
                      label={t("booking.send_sms")}
                      checked={sendSms}
                      onChange={(e) => setSendSms(e.target.checked)}
                      className="me-3"
                    />
                    <Form.Check
                      type="switch"
                      id="send-email-switch"
                      label={t("booking.send_email")}
                      checked={sendEmail}
                      onChange={(e) => setSendEmail(e.target.checked)}
                      className="me-3"
                    />
                    <Button 
                      variant="success" 
                      onClick={handleApprove}
                      disabled={updating}
                      size="sm"
                    >
                      <FaCheck className="me-1" /> {t("booking.approve")}
                    </Button>
                    <Button 
                      variant="danger" 
                      onClick={() => setShowRejectModal(true)}
                      disabled={updating}
                      size="sm"
                    >
                      <FaTimes className="me-1" /> {t("booking.reject")}
                    </Button>
                  </>
                )}
                
                {transport.status === 'approved' && (
                  <Button 
                    variant="warning" 
                    onClick={handleSetPending}
                    disabled={updating}
                    size="sm"
                  >
                    {t("booking.set_pending")}
                  </Button>
                )}

                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handleViewTrack(transport.booking_id)}
                  className="mx-1"
                >
                  <FaTruck /> {t("track")}
                </Button>

                {transport.order && transport.status !== 'completed' && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleViewLiveTracking(transport.order.id)}
                    className="mx-1"
                  >
                    <FaLocationArrow /> {t("live_tracking")}
                  </Button>
                )}

                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handleViewOrder(transport.id)}
                  className="mx-1"
                >
                  <TiThList /> {t("booking.orders")}
                  <Badge bg="secondary" pill className="ms-2">
                    {transport.order_count || 0}
                  </Badge>
                </Button>
              </div>
            </Col>

            {/* Payment Management */}
            <Col md={6} className="mb-3">
              <h6 className="fw-bold mb-3 text-success">
                <FaMoneyBillWave className="me-2" />
                {t("booking.payment_management")}
              </h6>
              <div className="d-flex flex-wrap gap-2">
                {transport.payment_status === 0 && (
                  <Button 
                    variant={transport.payment_status ? "success" : "outline-primary"}
                    onClick={handleMarkPaid}
                    disabled={updating || transport.payment_status}
                    size="sm"
                  >
                    <FaEuroSign className="me-1" /> {t("booking.mark_paid")}
                  </Button>
                )}
                
                {hasAssignOrder && (
                  <Button 
                    variant="outline-primary"
                    onClick={() => setShowDriversModal(true)}
                    size="sm"
                  >
                    <FaTruck className="me-2" />
                    {t("select_driver")}
                  </Button>
                )}
                {drivers.id && (
                  <div className="mt-2 p-2 bg-light rounded">
                    <strong>{t('selected_driver')}: </strong>
                    {drivers.firstname} {drivers.lastname}
                  </div>
                )}


                {(hasAvailableOrder || (transport.status === 'approved' && transport.status !== 'completed')) && (
                  <Button 
                    variant="primary" 
                    onClick={() => setCreateOrderModal(true)}
                    className="d-flex align-items-center justify-content-center"
                    size="sm"
                  >
                    <FaPlus className="me-2" />
                    {t("booking.create_order")}
                  </Button>
                )}

              </div>
            </Col>
          </Row>

          {/* Navigation and Dangerous Actions */}
          <Row className="mt-3 pt-3 border-top">
            <Col className="d-flex flex-wrap gap-2 justify-content-between">
              <div className="d-flex flex-wrap gap-2">
                <Button variant="outline-secondary" onClick={handleBack} size="sm">
                  <FaArrowLeft className="me-1" />
                  {t("booking.back")}
                </Button>
                
                <Button 
                  variant="outline-danger" 
                  onClick={handleDeleteTransport}
                  size="sm"
                >
                  <FaTrashAlt className="me-1" />
                  {t("delete")}
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    );
  };

  // Route Information
  const renderRouteInfo = () => {
    if (!transport) return null;

    return (
      <Card className="mb-4 shadow-sm">
        <Card.Header className="fw-bold card-title">
          {t("booking.route_info")}
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <div className="border-start border-3 border-success ps-3 mb-4">
                <h6 className="text-success fw-bold mb-2">
                  <FaMapMarkerAlt className="me-1" />
                  {t("booking.loading_info")}
                </h6>
                <p className="mb-1 fw-bold">{transport.booking_from}</p>
                <small className="text-muted">
                  • {t("booking.date")}: {transport.loading_date} 
                  <br />
                  • {t("booking.time")}: {transport.loading_time}
                </small>
              </div>
            </Col>
            <Col md={6}>
              <div className="border-start border-3 border-danger ps-3 mb-4">
                <h6 className="text-danger fw-bold mb-2">
                  <FaMapMarkerAlt className="me-1" />
                  {t("booking.unloading_info")}
                </h6>
                <p className="mb-1 fw-bold">{transport.booking_to}</p>
                <small className="text-muted">
                  • {t("booking.date")}: {transport.unloading_date}
                  <br />
                  • {t("booking.time")}: {transport.unloading_time}
                </small>
              </div>
            </Col>
          </Row>
          
          <Row className="mt-3 pt-3 border-top">
            <Col md={4}>
              <div className="text-center">
                <small className="text-muted d-block">{t("booking.distance")}</small>
                <h5 className="fw-bold text-primary">{transport.distance}</h5>
              </div>
            </Col>
            <Col md={4}>
              <div className="text-center">
                <small className="text-muted d-block">{t("duration")}</small>
                <h5 className="fw-bold text-info">{transport.total_duration}</h5>
              </div>
            </Col>
            <Col md={4}>
              <div className="text-center">
                <small className="text-muted d-block">{t("booking.transport_type")}</small>
                <h5 className="fw-bold text-success text-capitalize">{t(transport.transport_type)}</h5>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    );
  };

  // Items Details
  const renderItemsDetails = () => {
    if (!transport || transport.transport_type === "car") return null;

    const itemType = transport.item_type || [];
    const numItems = transport.num_items || [];
    const itemWeight = transport.item_weight || [];
    const weight = transport.weight || [];
    const dimensions = transport.dimensions || [];

    return (
      <Card className="mb-4 shadow-sm">
        <Card.Header className="fw-bold card-title">
          {t("booking.items_details")}
        </Card.Header>
        <Card.Body>
          {itemType.length > 0 ? (
            <>
              <Table striped bordered hover responsive className="mb-4">
                <thead>
                  <tr>
                    <th width="30%">{t("booking.item_type")}</th>
                    <th width="15%">{t("booking.quantity")}</th>
                    <th width="20%">{t("booking.weight")}</th>
                    <th width="35%">{t("booking.dimensions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {itemType.map((item, index) => (
                    <tr key={index}>
                      <td className="fw-bold">{item}</td>
                      <td>{numItems[index] || '-'}</td>
                      <td>
                        {itemWeight[index] ? `${formatNumber(itemWeight[index])} kg` : 
                         weight[index] ? `${formatNumber(weight[index])} kg` : '-'}
                      </td>
                      {dimensions[index] ? (
                        <td>
                          {dimensions[index] } cm
                        </td>
                      ) : (
                        <td>-</td>
                      )} 
                    </tr>
                  ))}
                </tbody>
              </Table>
              
              <div className="bg-light p-3 rounded">
                <Row className="gap-row-3">
                  <Col md={6}>
                    <div className="text-center">
                      <small className="text-muted d-block">{t("booking.total_items")}</small>
                      <h5 className="fw-bold text-primary">{itemType.length}</h5>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="text-center">
                      <small className="text-muted d-block">{t("booking.total_weight")}</small>
                      <h5 className="fw-bold text-success">{formatNumber(transport.total_weight)} kg</h5>
                    </div>
                  </Col>
                  {/* show car details if type = car */}
                  {transport.transport_type === "car" && (
                    <>
                      <Col md={6}>
                        <div className="text-center">
                          <small className="text-muted d-block">{t("booking.total_weight")}</small>
                          <h5 className="fw-bold text-success">{formatNumber(transport.total_weight)} kg</h5>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="text-center">
                          <small className="text-muted d-block">{t("booking.total_weight")}</small>
                          <h5 className="fw-bold text-success">{formatNumber(transport.total_weight)} kg</h5>
                        </div>
                      </Col>
                    </>
                  )}
                </Row>
              </div>
            </>
          ) : (
            <div className="text-center py-4">
              <FaBox className="text-muted mb-3" size={48} />
              <h5 className="text-muted">{t("booking.no_items")}</h5>
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  const renderCarDetails = () => {
    if (!transport || transport.transport_type !== "car") return null;

    const itemType = transport.item_type || [];
    const numItems = transport.num_items || [];
    const itemWeight = transport.item_weight || [];
    const boardNumber = transport.board_number || [];
    const makingYear = transport.making_year || [];
    const driving = transport.driving || [];
    const dimensions = transport.dimensions || [];

    return (
      <Card className="mb-4 shadow-sm">
        <Card.Header className="fw-bold card-title">
          {t("booking.cars_details")}
        </Card.Header>
        <Card.Body>
          {itemType.length > 0 ? (
            <>
              {itemType.map((item, index) => (
                <div key={index} className="mb-3 p-3 border rounded bg-light">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <Badge bg="primary">#{index + 1}</Badge>
                    <span className="fw-bold">{numItems?.[index] || 1} × {item}</span>
                  </div>
                  <div className="row text-muted flex-wrap">
                    <div className="col-6 mb-1">
                      <FaRulerCombined className="me-1" /> {dimensions?.[index] || '-'} cm
                    </div>
                    <div className="col-6 mb-1">
                      <FaWeightHanging className="me-1" />
                      {t('booking.weight')}: {itemWeight?.[index] || 0} kg
                    </div>
                      <div className="col-6 mb-1">
                        <AiFillIdcard className="me-1" />
                        {t('booking.boardNumber')}: {boardNumber?.[index] || '-'}
                      </div>
                    <div className="col-6 mb-1">
                      <FaCalendar className="me-1" />
                      {t('booking.makingYear')}: {makingYear?.[index] || '-'}
                    </div>
                    <div className="col-6 mb-1">
                      <HiWrenchScrewdriver className="me-1" />
                      {t('booking.driving')}: {driving?.[index] === "1" ? t('yes') : t('no')}
                    </div>
                  </div>
                </div>
              ))}
              
              <div className="bg-light p-3 rounded">
                <Row className="gap-row-3">
                </Row>
              </div>
            </>
          ) : (
            <div className="text-center py-4">
              <FaBox className="text-muted mb-3" size={48} />
              <h5 className="text-muted">{t("booking.no_items")}</h5>
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  const InfoRow = ({ label, value }) => {
  if (!value && value !== 0) return null;

  return (
    <tr>
      <td className="fw-bold">{label}:</td>
      <td>{value}</td>
    </tr>
  );
};

const BooleanRow = ({ label, value, details }) => {
  if (value === undefined || value === null) return null;

  const isYes = value === '1';

  return (
    <div className="detail-row d-flex justify-content-between align-items-start">
      <div>
        <strong>{label}</strong>: {t(isYes ? "yes" : "no")}
      </div>
      {isYes && details && (
        <div className="small-muted">{details}</div>
      )}
    </div>
  );
};

  // Additional Information
  const renderAdditionalInfo = () => {
  if (!transport) return null;

  return (
    <Card className="mb-4 shadow-sm">
      <Card.Header className="fw-bold card-title">
        {t("booking.additional_info")}
      </Card.Header>

      <Card.Body>
        <Row>
          {/* Loading */}
          <Col md={6}>
            <h6 className="fw-bold text-primary mb-3">
              {t("booking.loading_details")}
            </h6>

            <Table size="sm" borderless>
              <tbody>
                <InfoRow
                  label={t("booking.load_helping")}
                  value={getLoadHelpingText(transport.load_helping)}
                />
                <InfoRow
                  label={t("booking.loading_elevator")}
                  value={getBooleanText(transport.loading_elevator)}
                />
                <InfoRow
                  label={t("booking.loading_floor")}
                  value={transport.loading_floor || "-"}
                />
                <InfoRow
                  label={t("booking.parking")}
                  value={transport.parking || "-"}
                />
              </tbody>
            </Table>
          </Col>

          {/* Unloading */}
          <Col md={6}>
            <h6 className="fw-bold text-primary mb-3">
              {t("booking.unloading_details")}
            </h6>

            <Table size="sm" borderless>
              <tbody>
                <InfoRow
                  label={t("booking.unloading_elevator")}
                  value={getBooleanText(transport.unloading_elevator)}
                />
                <InfoRow
                  label={t("booking.unloading_floor")}
                  value={transport.unloading_floor || "-"}
                />
              </tbody>
            </Table>
          </Col>

          {/* Extra */}
          <Col md={12}>
            {transport.delivery_as && (
              <div className="detail-row d-flex gap-2">
                <strong>{t("booking.delivery_as")}:</strong>
                <span className="small-muted">
                  {t(transport.delivery_as)}
                </span>
              </div>
            )}

            <BooleanRow
              label={t("booking.fragile")}
              value={transport.malfunction}
              details={transport.malfunction_details}
            />

            <BooleanRow
              label={t("booking.special_handling")}
              value={transport.special_treatment}
              details={transport.special_treatment_details}
            />

            <BooleanRow
              label={t("booking.breakable")}
              value={transport.item_breakable}
              details={transport.item_breakable_details}
            />
          </Col>
        </Row>

        {/* Description */}
        {transport.description && (
          <div className="mt-4 p-3 bg-light rounded">
            <h6 className="fw-bold text-primary mb-2">
              {t("booking.description")}
            </h6>
            <p className="mb-0">{transport.description}</p>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

  // User Information
  const renderUserInfo = () => {
    if (!transport) return null;

    return (
      <Card className="mb-4 shadow-sm">
        <Card.Header className="fw-bold card-title d-flex justify-content-between align-items-center flex-wrap">
          {t("booking.user_info")}
          {transport.user?.id && (
            <>
              <div className="d-flex gap-3">
                <button
                  className="btn btn-primary"
                  onClick={() => navigate(`/admin/customers/details/${transport.user.id}`)}
                >
                  <FaUserAlt className="" />
                </button>
                <button
                  className="btn btn-success"
                  onClick={() => navigate(`/admin/client-list/chat-client/${transport.user.id}`)}
                >
                  <FaFacebookMessenger className="" />
                </button>
              </div>
            </>
          )}
        </Card.Header>
        <Card.Body>
          {transport.user ? (
            <>
              <p><strong>{t("user_name")}:</strong> {transport.user.user_name}</p>
              <p><strong>{t("phone")}:</strong> {transport.user.user_phone}</p>
              <p><strong>{t("user_email")}:</strong> {transport.user.user_email}</p>
            </>
          ) : (
            <p className="text-muted">{t('no_user_found')}</p>
          )}
        </Card.Body>
      </Card>
    );
  };

  const renderDriverInfo = () => {
    if (!transport) return null;

    return (
      <Card className="mb-4 shadow-sm">
        <Card.Header className="fw-bold card-title d-flex justify-content-between align-items-center flex-wrap">
          {t("booking.driver_info")}
          
          {transport.order && transport.order.driver_role === 'Driver' ? (
            <>
              <div className="d-flex gap-3">
                <button
                  className="btn btn-primary"
                  onClick={() => navigate(`/admin/drivers/details/${transport.order.order_driver}`)}
                >
                  <RiUserLocationFill className="" />
                </button>
                <button
                  className="btn btn-success"
                  onClick={() => navigate(`/admin/driver-list/chat-driver/${transport.order.order_driver}`)}
                >
                  <FaFacebookMessenger className="me-1" />
                </button>
              </div>
            </>
          ) : transport.order && transport.order.driver_role === 'Driver Company' ? (
            <>
              <div className="d-flex gap-3">
                <button
                  className="btn btn-primary"
                  onClick={() => navigate(`/admin/drivers-company/details/${transport.order.order_driver}`)}
                >
                  <RiUserLocationFill className="" />
                </button>
                <button
                  className="btn btn-success"
                  onClick={() => navigate(`/admin/driver-company-list/chat-driver-company/${transport.order.order_driver}`)}
                >
                  <FaFacebookMessenger className="me-1" />
                </button>
              </div>
            </>
          ) : null}
        </Card.Header>
        <Card.Body>
          {transport.order ? (
            <>
              <p><strong>{t("driver_name")}:</strong> {transport.order.driver_name}</p>
              <p><strong>{t("driver_role")}:</strong> {transport.order.driver_role}</p>
              <p><strong>{t("phone")}:</strong> {transport.order.phone_number}</p>
              <p><strong>{t("vehicle_type")}:</strong> {transport.order.vehicle_type}</p>
              <p><strong>{t("vehicle_number")}:</strong> {transport.order.vehicle_number}</p>
              <p><strong>{t("current_location")}:</strong> {transport.order.current_location}</p>
              <p><strong>{t("status")}:</strong> {getStatusBadge(transport.order.status)}</p>
            </>
          ) : (
            <p className="text-muted">{t('no_driver_assigned')}</p>
          )}
        </Card.Body>
      </Card>
    );
  };

  // Payment & Commission
  const renderPaymentCommission = () => {
    if (!transport) return null;

    return (
      <Card className="mb-4 shadow-sm">
        <Card.Header className="fw-bold card-title d-flex justify-content-between align-items-center">
          {t("booking.payment_commission")}
        </Card.Header>
        <Card.Body>
          <div className="p-3 border rounded bg-light">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
              <Form.Check
                type="switch"
                id="apply-commission-switch"
                label="حساب نسبة المنصة"
                checked={toBoolean(transport.apply_commission)}
                onChange={handleToggleCommission}
                disabled={updating || hasAcceptedOrder}
                className="mb-0"
              />
              {hasAcceptedOrder && (
                <small className="text-muted">لا يمكن التعديل بعد قبول عرض</small>
              )}
            </div>
            <h6 className="fw-bold text-muted mb-3">{t("booking.commission")}</h6>
            <div className="d-flex align-items-center mb-3">
              <div className="flex-grow-1">
                <h4 className="fw-bold text-primary mb-0">{transport.platform_commission}%</h4>
              </div>
            </div>
            <InputGroup size="sm">
              <Form.Control
                type="number"
                value={commission}
                onChange={(e) => setCommission(Number(e.target.value))}
                min="0"
                max="100"
                placeholder="0-100"
                disabled={updating || hasAcceptedOrder}
              />
              <InputGroup.Text>%</InputGroup.Text>
              <Button 
                variant="primary"
                onClick={handleAddCommission}
                disabled={updating || hasAcceptedOrder}
              >
                {updating ? <Spinner size="sm" /> : t("update")}
              </Button>
            </InputGroup>
          </div>
        </Card.Body>
      </Card>
    );
  };

  // Notes Section
  const renderNotesSection = () => {
    return (
      <Card className="mb-4 shadow-sm">
        <Card.Header className="fw-bold card-title">
          {t("booking.notes")}
        </Card.Header>
        <Card.Body>
          <Form>
            <Form.Group>
              <Form.Label className="fw-bold">{t("booking.notes_label")}</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={4} 
                placeholder={t("booking.notes_placeholder")}
                value={notizen}
                onChange={(e) => setMessage(e.target.value)}
                className="mb-3"
              />
            </Form.Group>
            <div className="d-flex justify-content-end">
              <Button 
                variant="primary" 
                onClick={handleSendMessage}
                className="px-4"
              >
                {t("booking.save_notes")}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    );
  };

  // Admin Logs
  const renderAdminLogs = () => {
    if (!transport) return null;

    return (
      <Card className="shadow-sm">
        <Card.Header className="fw-bold card-title">
          {t("admin_logs")}
        </Card.Header>
        <Card.Body>
          {transport.admin_logs && transport.admin_logs.length > 0 ? (
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead className="table-dark">
                  <tr>
                    <th>{t("admin_name")}</th>
                    <th>{t("admin_email")}</th>
                    <th>{t("action")}</th>
                    <th>{t("description")}</th>
                    <th>{t("created_at")}</th>
                    {isSuperAdmin && <th>{t("actions")}</th>}
                  </tr>
                </thead>
                <tbody>
                  {transport.admin_logs.map((entry) => (
                    <tr key={entry.id}>
                      <td>{entry.admin_name || "N/A"}</td>
                      <td>{entry.admin_email || "N/A"}</td>
                      <td>{entry.action}</td>
                      <td>{entry.description}</td>
                      <td>{new Date(entry.created_at).toLocaleString('de-DE')}</td>
                      {isSuperAdmin && (
                        <td>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => {
                              setDeleteLogId(entry.id);
                              setShowDeleteLog(true);
                            }}
                            title={t("delete")}
                          >
                            <FaTrashAlt />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-4">
              <FaHistory className="text-muted mb-3" size={48} />
              <h5 className="text-muted">{t("booking.no_logs")}</h5>
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  if (loading) {
    return (
      <Container fluid className="p-1 bg-light min-vh-100 d-flex justify-content-center align-items-center">
        <div className="text-center">
          <Spinner animation="border" variant="primary" size="lg" />
          <p className="mt-3 text-muted">{t('loading')}</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="p-1 bg-light min-vh-100">
        <Alert variant="danger" className="m-3">
          <div className="d-flex align-items-center">
            <FaTimes className="me-2" />
            {t(error)}
          </div>
          <Button variant="outline-danger" className="mt-2" onClick={handleBack}>
            <FaArrowLeft className="me-1" />
            {t('back_to_list')}
          </Button>
        </Alert>
      </Container>
    );
  }

  if (!transport) {
    return (
      <Container fluid className="p-1 bg-light min-vh-100">
        <Alert variant="warning" className="m-3">
          <div className="d-flex align-items-center">
            <FaInfoCircle className="me-2" />
            {t('transport_not_found')}
          </div>
          <Button variant="outline-warning" className="mt-2" onClick={handleBack}>
            <FaArrowLeft className="me-1" />
            {t('back_to_list')}
          </Button>
        </Alert>
      </Container>
    );
  }
  
  return (
    <Container fluid className="p-1 bg-light min-vh-100">
      {renderHeader()}
      {(transport.status === 'cancelled' || transport.status === 'canceled' || transport.deleted_reason || transport.cancelled_reason) && (
        <Alert variant="danger" className="mb-4">
          <strong>{t("cancellation_reason")}:</strong>{" "}
          {transport.deleted_reason || transport.cancelled_reason || t("no_cancellation_reason")}
        </Alert>
      )}
      {renderActionsSection()}

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4" fill>        
        <Tab eventKey="details" title={t("booking.details")} className="border-0">
          <div className="mt-3">
            <Row>
              <Col md={8}>
                {renderRouteInfo()}
                {renderItemsDetails()}
                {renderCarDetails()}
                {renderAdditionalInfo()}
                <ImageGallery images={transport.images} />
              </Col>
              <Col md={4}>
                {renderUserInfo()}
                {renderDriverInfo()}
                {transport.status === 'pending' || transport.status === 'approved' && (
                  renderPaymentCommission()
                )}

                {/* {renderNotesSection()} */}
              </Col>
            </Row>
          </div>
        </Tab>
        
        <Tab eventKey="logs" title={t("admin_logs")} className="border-0">
          <div className="mt-3">
            {renderAdminLogs()}
          </div>
        </Tab>
      </Tabs>

      {/* Reject Modal */}
      <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t("booking.reject_transport")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>{t("booking.reject_reason")}</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder={t("booking.enter_reject_reason")}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRejectModal(false)}>
            {t("cancel")}
          </Button>
          <Button variant="danger" onClick={handleReject} disabled={updating}>
            {updating ? <Spinner size="sm" /> : t("transport.reject")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Drivers Modal */}
      <Modal show={showDriversModal} onHide={() => setShowDriversModal(false)} centered width="auto" className="select_driver">
        <Modal.Header closeButton>
          <Modal.Title>{t('select_driver')}</Modal.Title>
        </Modal.Header>

        {/* Search Bar */}
        <Row className="m-3">
          <Col md={8}>
            <InputGroup>
              <InputGroup.Text><FaSearch /></InputGroup.Text>
              <Form.Control
                type="text"
                placeholder={t('search_by_email')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <Button 
                  variant="outline-secondary" 
                  onClick={clearSearch}
                  title={t("reset_search")}
                >
                  ✕
                </Button>
              )}
            </InputGroup>
          </Col>
        </Row>

        <Modal.Body>
          {loading ? (
            <div className="text-center">
              <Spinner />
            </div>
          ) : (
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>{t('name')}</th>
                  <th>{t('email')}</th>
                  <th>{t('company')}</th>
                  <th>{t('vehicle_type')}</th>
                  <th>{t('action')}</th>
                </tr>
              </thead>
              <tbody>
                {drivers.map(driver => (
                  <tr key={driver.id}>
                    <td>{driver.firstname} {driver.lastname}</td>
                    <td>
                      {searchTerm ? (
                        <span 
                          dangerouslySetInnerHTML={{
                            __html: driver.email?.replace(
                              new RegExp(searchTerm, 'gi'), 
                              match => `<mark class="bg-warning">${match}</mark>`
                            )
                          }} 
                        />
                      ) : (
                        driver.email
                      )}
                    </td>
                    <td>{driver.company_name}</td>
                    <td>{driver.vehicle_type}</td>
                    <td>
                      <Button
                        size="sm"
                        variant={transport.customer_id === driver.id ? 'success' : 'outline-primary'}
                        onClick={() => selectDriver(driver)}
                      >
                        {transport.customer_id === driver.id ? t('selected') : t('select')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
      </Modal>
      {/* Create Order Modal */}
      <Modal show={createOrderModal} onHide={() => setCreateOrderModal(false)} centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="fw-bold">
            <FaFileInvoice className="me-2 text-primary" />
            {t("booking.create_new_order")}
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">
                {t("booking.price")}*
              </Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <FaEuroSign />
                </InputGroup.Text>
                <Form.Control
                  type="number"
                  placeholder={t("booking.enter_price")}
                  value={orderData.price}
                  onChange={(e) => handleOrderDataChange('price', e.target.value)}
                  min="0"
                  step="0.01"
                />
              </InputGroup>
            </Form.Group>

            {/* حقل المعلومات */}
            <Form.Group className="mb-3">
              <Form.Label className="fw-bold">
                {t("booking.info")}
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder={t("booking.enter_order_info")}
                value={orderData.info}
                onChange={(e) => handleOrderDataChange('info', e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        
        <Modal.Footer>
          <Button 
            variant="outline-secondary" 
            onClick={() => {
              setCreateOrderModal(false);
              setOrderData({ price: '' });
            }}
            disabled={updating}
          >
            {t("cancel")}
          </Button>
          
          <Button 
            variant="primary" 
            onClick={createOrder}
            disabled={updating || !orderData.price}
            className="d-flex align-items-center"
          >
            {updating ? (
              <>
                <Spinner size="sm" className="me-2" />
                {t("booking.creating")}
              </>
            ) : (
              <>
                <FaCheck className="me-2" />
                {t("booking.create_order")}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Log Confirmation Modal */}
      <Modal show={showDeleteLog} onHide={() => setShowDeleteLog(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t("confirm_delete")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("are_you_sure_delete_log")}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteLog(false)}>
            {t("cancel")}
          </Button>
          <Button variant="danger" onClick={confirmDeleteLog} disabled={loading}>
            {loading ? <Spinner size="sm" /> : t("delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
